import { useEffect, useMemo, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';
import { DateTimePicker } from '@/components/DateTimePicker';
import { useDialogFocusReturn } from '@/hooks/useDialogFocusReturn';
import { hashUserId } from '@/lib/hash/userIdHash';

import { StudentMultiSelect } from './StudentMultiSelect';
import type { RosterHashMapResult } from './useRosterHashMap';
import type { ResolvedExtensionMember, TimeExtensionGroup } from './types';
import styles from './ExtensionGroupDialog.module.css';

interface ExtensionGroupDialogProps {
  courseWideEndTimeUtcMilli: string | null | undefined;
  /** Where focus goes on close when the button that opened the dialog has gone. */
  focusFallback?: () => HTMLElement | null;
  group?: TimeExtensionGroup | null;
  onOpenChange(nextOpen: boolean): void;
  onSave(group: TimeExtensionGroup): void;
  open: boolean;
  rosterHashMap: RosterHashMapResult | null | undefined;
}

function parseTimestamp(value: string | null | undefined): number | null {
  if (!value) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function sortMembers(
  firstMember: ResolvedExtensionMember,
  secondMember: ResolvedExtensionMember,
) {
  const lastNameCompare = firstMember.lastName.localeCompare(secondMember.lastName);

  if (lastNameCompare !== 0) {
    return lastNameCompare;
  }

  const firstNameCompare = firstMember.firstName.localeCompare(
    secondMember.firstName,
  );

  if (firstNameCompare !== 0) {
    return firstNameCompare;
  }

  return firstMember.userId.localeCompare(secondMember.userId);
}

export function ExtensionGroupDialog({
  courseWideEndTimeUtcMilli,
  focusFallback,
  group,
  onOpenChange,
  onSave,
  open,
  rosterHashMap,
}: ExtensionGroupDialogProps) {
  // A group's own Edit button is drawn again once a save renames the group.
  const focusReturn = useDialogFocusReturn({ fallback: focusFallback });
  const [displayName, setDisplayName] = useState('');
  const [endTimestamp, setEndTimestamp] = useState<number | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [unknownHashedStudentIds, setUnknownHashedStudentIds] = useState<string[]>(
    [],
  );
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const rosterStudents = useMemo(
    () => [...(rosterHashMap?.profilesById.values() ?? [])].sort(sortMembers),
    [rosterHashMap],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setDisplayName(group?.displayName ?? '');
    setEndTimestamp(parseTimestamp(group?.endTimeUtcMilli));
    setHasSubmitted(false);
    setIsSaving(false);

    const resolvedUserIds: string[] = [];
    const unresolvedHashes: string[] = [];

    group?.hashedStudentIds.forEach((hash) => {
      const profile = rosterHashMap?.hashToProfile.get(hash) ?? null;

      if (profile) {
        resolvedUserIds.push(profile.userId);
      } else {
        unresolvedHashes.push(hash);
      }
    });

    setSelectedUserIds(resolvedUserIds);
    setUnknownHashedStudentIds(unresolvedHashes);
  }, [group, open, rosterHashMap]);

  const trimmedDisplayName = displayName.trim();
  const courseWideTimestamp = parseTimestamp(courseWideEndTimeUtcMilli);
  const displayNameError =
    hasSubmitted && !trimmedDisplayName ? 'Display name is required.' : null;
  const dateError =
    hasSubmitted && endTimestamp === null ? 'Extension end date is required.' : null;
  const isEarlierThanCourse =
    endTimestamp !== null &&
    courseWideTimestamp !== null &&
    endTimestamp < courseWideTimestamp;
  const isInPast = endTimestamp !== null && endTimestamp < Date.now();

  async function handleSave() {
    setHasSubmitted(true);

    if (!trimmedDisplayName || endTimestamp === null) {
      return;
    }

    setIsSaving(true);

    try {
      const hashedStudentIds = Array.from(
        new Set([
          ...(await Promise.all(
            selectedUserIds.map((userId) => hashUserId(userId)),
          )),
          ...unknownHashedStudentIds,
        ]),
      );

      onSave({
        displayName: trimmedDisplayName,
        endTimeUtcMilli: String(endTimestamp),
        hashedStudentIds,
      });
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content} {...focusReturn}>
          <div className={styles.header}>
            <Dialog.Title className={styles.title}>
              {group ? 'Edit Extension Group' : 'Add Extension Group'}
            </Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.description}>
                Choose the students in this group and the date their extension
                ends for this chapter.
              </p>
            </Dialog.Description>
          </div>

          <div className={styles.body}>
            <label className={styles.field} htmlFor="extension-display-name">
              <span className={styles.label}>Display name</span>
              <input
                className={styles.input}
                id="extension-display-name"
                onChange={(event) => setDisplayName(event.target.value)}
                value={displayName}
              />
            </label>
            {displayNameError ? (
              <p aria-live="polite" className={styles.error} role="alert">
                {displayNameError}
              </p>
            ) : null}

            <StudentMultiSelect
              onSelectedUserIdsChange={setSelectedUserIds}
              selectedUserIds={selectedUserIds}
              students={rosterStudents}
              unknownHashedStudentIds={unknownHashedStudentIds}
            />

            {selectedUserIds.length === 0 &&
            unknownHashedStudentIds.length === 0 ? (
              <p className={styles.hint}>
                This group has no students. It can be saved, but it will not
                affect any due dates until students are added.
              </p>
            ) : null}

            <DateTimePicker
              id="extension-end-time"
              label="Extension end date"
              onChange={setEndTimestamp}
              value={endTimestamp}
            />
            {dateError ? (
              <p aria-live="polite" className={styles.error} role="alert">
                {dateError}
              </p>
            ) : null}
            {isEarlierThanCourse ? (
              <p className={styles.warning}>
                This extension date is earlier than the course due date. Students
                in this group will have LESS time, not more.
              </p>
            ) : null}
            {isInPast ? (
              <p className={styles.warning}>
                This extension date is in the past.
              </p>
            ) : null}
          </div>

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button variant="secondary">Cancel</Button>
            </Dialog.Close>
            <Button
              disabled={isSaving}
              onClick={() => {
                void handleSave();
              }}
              variant="primary"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
